import { type FormEvent, useState } from 'react';
import { useApi, useConsulta } from '@plataforma/web/api';
import { fechaCorta, fechaHoraLocal, mxn } from '../formato';
import type { Monto } from '../paginas/tipos';

export interface Trabajo {
  id: string;
  proveedorNombre: string;
  estado: 'adjudicado' | 'en_proceso' | 'entregado' | 'aceptado' | 'cancelado';
  estadoPago: 'pendiente' | 'parcial' | 'pagado' | 'disputa';
  precio: Monto;
}
interface Pago {
  id: string;
  monto: Monto;
  metodo: string;
  referencia: string | null;
  fecha: string;
}

const ESTRELLAS = [5, 4, 3, 2, 1];

/**
 * Trabajo adjudicado a un proveedor: avance, aceptación con calificación (su monto pasa a costo
 * real del vehículo) y pagos registrados por fuera de la plataforma, con su seguimiento.
 */
export function TrabajoProveedor({ t, alCambiar }: { t: Trabajo; alCambiar: () => void }) {
  const api = useApi();
  const pagos = useConsulta<Pago[]>(`/workshop/proveedores/trabajos/${t.id}/pagos`);
  // Pago en línea (Stripe Connect a la cuenta del proveedor): lo ofrece Payments.
  const enLinea = useConsulta<
    { trabajoId: string; pagadoEnLinea: boolean; pagoEnLinea: boolean }[]
  >('/commerce/pagos/trabajos');
  const deEste = enLinea.datos?.find((x) => x.trabajoId === t.id);
  const pagarEnLinea = () =>
    hacer(async () => {
      const { url } = await api<{ url: string }>('POST', `/commerce/pagos/trabajos/${t.id}`);
      window.location.assign(url);
    });
  const [error, setError] = useState<string | null>(null);
  const [aceptando, setAceptando] = useState(false);
  const hacer = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
      alCambiar();
      pagos.recargar();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  const aceptar = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    void hacer(() =>
      api('POST', `/workshop/proveedores/trabajos/${t.id}/aceptar`, {
        calificacion: {
          precio: Number(f.get('precio')),
          plazo: Number(f.get('plazo')),
          calidad: Number(f.get('calidad')),
          ...(f.get('comentario') && { comentario: f.get('comentario') }),
        },
      }),
    );
  };
  const pagar = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const forma = e.currentTarget;
    const f = new FormData(forma);
    void hacer(async () => {
      await api('POST', `/workshop/proveedores/trabajos/${t.id}/pagos`, {
        monto: {
          cantidad: String(f.get('cantidad')),
          moneda: 'MXN',
          fecha: String(f.get('fecha')),
        },
        metodo: f.get('metodo'),
        ...(f.get('referencia') && { referencia: f.get('referencia') }),
      });
      forma.reset();
    });
  };
  const cerrado = t.estado === 'aceptado' || t.estado === 'cancelado';
  return (
    <section className="seccion">
      <header>
        <h2>Trabajo de {t.proveedorNombre}</h2>
        <div style={{ flex: 1 }} />
        <span className={`etiqueta ${t.estado === 'aceptado' ? 'exito' : ''}`}>
          {t.estado.replace('_', ' ')}
        </span>
        <span
          className={`etiqueta ${t.estadoPago === 'pagado' ? 'exito' : t.estadoPago === 'disputa' ? 'peligro' : 'aviso'}`}
        >
          pago {t.estadoPago}
        </span>
      </header>
      <div
        className="cuerpo"
        style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}
      >
        <span>Precio adjudicado: {mxn(t.precio.equivalenteMxn)}</span>
        <div style={{ flex: 1 }} />
        {!cerrado && t.estado === 'adjudicado' && (
          <button
            className="boton"
            onClick={() =>
              void hacer(() =>
                api('PUT', `/workshop/proveedores/trabajos/${t.id}/estado`, {
                  estado: 'en_proceso',
                }),
              )
            }
          >
            Marcar en proceso
          </button>
        )}
        {!cerrado && t.estado !== 'entregado' && (
          <button
            className="boton"
            onClick={() =>
              void hacer(() =>
                api('PUT', `/workshop/proveedores/trabajos/${t.id}/estado`, {
                  estado: 'entregado',
                }),
              )
            }
          >
            Marcar entregado
          </button>
        )}
        {!cerrado && (
          <button className="boton primario" onClick={() => setAceptando(true)}>
            Aceptar y calificar
          </button>
        )}
        <button
          className="boton"
          onClick={() =>
            void hacer(() =>
              api('PUT', `/workshop/proveedores/trabajos/${t.id}/disputa`, {
                disputa: t.estadoPago !== 'disputa',
              }),
            )
          }
        >
          {t.estadoPago === 'disputa' ? 'Quitar disputa' : 'Marcar pago en disputa'}
        </button>
      </div>
      {aceptando && !cerrado && (
        <form
          className="cuerpo formulario"
          onSubmit={aceptar}
          style={{ borderTop: '1px solid var(--borde-suave)' }}
        >
          {(['precio', 'plazo', 'calidad'] as const).map((k) => (
            <label key={k} className="campo">
              <span>
                {k === 'precio' ? 'Precio cumplido' : k === 'plazo' ? 'Plazo cumplido' : 'Calidad'}
              </span>
              <select name={k} defaultValue={5}>
                {ESTRELLAS.map((n) => (
                  <option key={n} value={n}>
                    {'★'.repeat(n)}
                  </option>
                ))}
              </select>
            </label>
          ))}
          <label className="campo" style={{ gridColumn: '1 / -1' }}>
            <span>Comentario (opcional)</span>
            <input name="comentario" maxLength={1000} />
          </label>
          <div className="acciones">
            <span className="subtitulo">
              Al aceptarlo, su precio pasa a costo real de reparación del vehículo.
            </span>
            <button type="button" className="boton" onClick={() => setAceptando(false)}>
              Cancelar
            </button>
            <button className="boton primario">Aceptar trabajo</button>
          </div>
        </form>
      )}
      <div className="cuerpo" style={{ borderTop: '1px solid var(--borde-suave)' }}>
        <h3 style={{ fontSize: 14, marginBottom: 8 }}>Pagos al proveedor</h3>
        {pagos.datos && pagos.datos.length > 0 && (
          <table>
            <tbody>
              {pagos.datos.map((p) => (
                <tr key={p.id}>
                  <td>{fechaCorta(p.fecha)}</td>
                  <td>{p.metodo}</td>
                  <td>{p.referencia ?? '—'}</td>
                  <td className="num">{mxn(p.monto.equivalenteMxn)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {t.estado !== 'cancelado' &&
          t.estadoPago !== 'pagado' &&
          deEste?.pagoEnLinea &&
          !deEste.pagadoEnLinea && (
            <div className="acciones" style={{ marginTop: 10 }}>
              <button className="boton primario" onClick={() => void pagarEnLinea()}>
                Pagar en línea al proveedor
              </button>
              <small className="subtitulo">
                Con tarjeta por la plataforma; el pago queda registrado solo. O regístralo abajo si
                pagaste por fuera.
              </small>
            </div>
          )}
        {t.estado !== 'cancelado' && t.estadoPago !== 'pagado' && (
          <form className="formulario" onSubmit={pagar} style={{ marginTop: 10 }}>
            <label className="campo">
              <span>Monto pagado (MXN)</span>
              <input name="cantidad" required inputMode="decimal" pattern="\d{1,12}(\.\d{1,2})?" />
            </label>
            <label className="campo">
              <span>Método</span>
              <select name="metodo" defaultValue="transferencia">
                <option value="transferencia">Transferencia</option>
                <option value="efectivo">Efectivo</option>
                <option value="cheque">Cheque</option>
                <option value="otro">Otro</option>
              </select>
            </label>
            <label className="campo">
              <span>Referencia</span>
              <input name="referencia" maxLength={120} placeholder="SPEI, folio…" />
            </label>
            <label className="campo">
              <span>Fecha</span>
              <input
                name="fecha"
                type="date"
                required
                defaultValue={fechaHoraLocal(new Date()).slice(0, 10)}
              />
            </label>
            <div className="acciones">
              <button className="boton">Registrar pago</button>
            </div>
          </form>
        )}
      </div>
      {error && (
        <p className="error" style={{ margin: 16 }}>
          {error}
        </p>
      )}
    </section>
  );
}
